import DreamdataAwareUiEvents from 'client/GrowthEngineering/Dreamdata/DreamdataAwareUiEvents';
import dreamdataConsent from 'client/GrowthEngineering/Dreamdata/dreamdataConsent';
import dreamdataCookielessEvents from 'client/GrowthEngineering/Dreamdata/dreamdataCookielessEvents';
import sanitizeCookielessTrackProps from 'client/GrowthEngineering/Dreamdata/sanitizeCookielessTrackProps';

/**
 * Checks the OptanonConsent cookie and the in-memory flag set once the standard pipeline has
 * been activated. The cookie check covers returning visitors before this page load's OneTrust
 * callback has run. Session revoke / ready-timeout decline wins over a stale consent cookie so
 * we do not queue into a declined standard pipeline.
 */
function isConsented() {
  if (window.DreamdataUiEvents && window.DreamdataUiEvents.consentDeclined) {
    return false;
  }
  return dreamdataConsent.hasDreamdataConsent() || !!window.dreamdataInitialized;
}

/**
 * Routes a track call to Dreamdata: standard when consented, cookieless otherwise.
 * Cookieless no-ops if the stub was never injected (returning consented → mid-session revoke);
 * that is intentional - pipeline is fixed at bootstrap, not hot-swapped. Wired for Signup
 * Submitted only (via EventManager). Cookieless tracks strip known PII keys. Guards on
 * window.analytics so ConfigService.disableAnalytics disables Dreamdata too.
 */
function track(eventName, props) {
  try {
    if (!window.analytics) {
      return;
    }

    if (isConsented()) {
      DreamdataAwareUiEvents().track(eventName, props);
    } else {
      dreamdataCookielessEvents.track(eventName, sanitizeCookielessTrackProps(props));
    }
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }
}

/**
 * Fires dreamdata.page(). SPA navigations pass origin+pathname as the page name so routes are
 * distinguishable; Dreamdata's context URL still comes from the document canonical.
 */
function page(...args) {
  try {
    if (!window.analytics) {
      return;
    }

    if (isConsented()) {
      DreamdataAwareUiEvents().page(...args);
    } else {
      dreamdataCookielessEvents.page(...args);
    }
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }
}

/**
 * Fire a Dreamdata page view for in-app navigations only. Skips the initial SPA route
 * resolution (empty from.matched) so it does not duplicate the vendor snippet's auto page().
 */
function pageAfterRouteChange(to, from) {
  try {
    if (!from || !from.matched || from.matched.length === 0) {
      return;
    }

    if (to && from && to.fullPath === from.fullPath) {
      return;
    }

    page(window.location.origin + window.location.pathname);
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }
}

export default {
  track,
  page,
  pageAfterRouteChange,
};
